import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  YouTube as TwitchIcon,
  CalendarToday as CalendarIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';

const QuizzJDGProject: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/Twitch/QuizzJDG.png"
          alt="Logo Quizz Hardcore du Joueur du Grenier"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Quizz Hardcore du Joueur du Grenier
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Zevent 2022
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Émission de quiz interactive développée spécialement pour le Zevent, sur une idée du Joueur du Grenier. Le système intègre différents 
        formats de questions (classiques, blindtests, images, vidéos) avec 
        une régie technique complète incluant buzzers physiques, intégration 
        VMix pour la diffusion et pupitres avec écrans de contrôle.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Fonctionnalités principales */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Fonctionnalités principales
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 2 
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Questions multiformats (texte, audio, vidéo)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Système de buzzers physiques
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Intégration VMix complète
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Gestion automatique des scènes
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Pupitres avec écrans de contrôle
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Affichage scores temps réel
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Habillage graphique Unity
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Diffusion live professionnelle
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Technologies */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Technologies utilisées
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['C#', 'Unity', 'Socket.IO', 'API VMix', '.NET', 'Hardware Integration', 'Real-time Graphics'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Intégration VMix avancée :</strong> Développement d'une interface de contrôle 
          sophistiquée pour la gestion automatique des scènes VMix, permettant l'affichage 
          dynamique de contenus multimédias (vidéos, images, sons) synchronisés avec le quiz.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Système de buzzers hardware :</strong> Intégration de buzzers physiques 
          avec détection précise des pressions et gestion des priorités pour assurer 
          une expérience de jeu équitable et responsive.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Synchronisation multi-écrans :</strong> Coordination en temps réel entre 
          l'affichage principal, les pupitres de contrôle et les écrans des participants 
          pour maintenir une cohérence parfaite de l'information.
        </Typography>
        <Typography variant="body2">
          <strong>Performance live critique :</strong> Architecture robuste capable de gérer 
          la pression d'un événement live majeur comme le Zevent, avec zéro tolérance 
          aux bugs et système de fallback pour assurer la continuité de l'émission.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Liens actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<TwitchIcon />}
          href="#"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8b5cf6 0%, #0f766e 100%)',
            },
          }}
        >
          Voir l'émission
        </Button>
      </Box>
    </Box>
  );
};

export default QuizzJDGProject;




